import { MAIN_FUNCTION_SORT_KEYS, type MainFunctionListItem, type MainFunctionSortKey } from "@seagames/contracts";
import { type ColumnDef, DataTable, IconButton, Icons, ListHeader, ListTag, ListToolbar, PrimaryCell, StatsStrip, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, createFileRoute, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";
import { ConfirmDeleteModal } from "../../components/common/confirm-delete-modal";
import { EditDeleteActions } from "../../components/common/edit-delete-actions";
import { timestampColumn, usageColumn } from "../../components/common/list-columns";
import { type MainFunctionFormTarget, MainFunctionFormModal } from "../../components/functions/main-function-form-modal";
import { deleteMainFunction, invalidateFunctions, mainFunctionStatsOptions, mainFunctionsListOptions } from "../../lib/functions/queries";
import { listSearch, searchText } from "../../lib/list-page/list-search";
import { useConfirmDelete } from "../../lib/list-page/use-confirm-delete";
import { sortSelect } from "../../lib/list-page/sort-select";
import { useListPage } from "../../lib/list-page/use-list-page";
import { organizationTypeChipOptions, topOrganizationTypeCells } from "../../lib/organization-types/chips";
import { organizationTypeOptionsOptions } from "../../lib/organization-types/queries";

export const Route = createFileRoute("/_app/settings/functions")({
	validateSearch: listSearch(MAIN_FUNCTION_SORT_KEYS, { category: searchText(), organization_type_id: z.string().uuid() }),
	component: FunctionsShell,
});

/** The drill-down ($mainFunctionId) nests under this route: when it matched, render it instead of the list. */
function FunctionsShell() {
	const params = useParams({ strict: false });
	return params.mainFunctionId !== undefined ? <Outlet /> : <MainFunctionsPage />;
}

/** The filters as the URL holds them. `""` = not set; the URL drops empties. */
interface MainFunctionFilters extends Record<string, unknown> {
	search: string;
	category: string;
	organizationTypeId: string;
}
const EMPTY_FILTERS: MainFunctionFilters = { search: "", category: "", organizationTypeId: "" };

function MainFunctionsPage() {
	const { t } = useTranslation("functions");
	const { t: tc } = useTranslation("common");
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const { formatNumber } = useUiStrings();
	const isSuper = session.active_role === "SUPER_ADMIN";
	const [form, setForm] = useState<MainFunctionFormTarget | null>(null);

	// No default sort in the URL on purpose — the API already orders by
	// (sort_order, code), the card hierarchy, when `sort` is undefined.
	const page = useListPage({
		search,
		navigate,
		listOptions: (q) => mainFunctionsListOptions({ ...q, category: search.category, organization_type_id: search.organization_type_id }),
		columnsKey: "main-functions",
		filters: {
			empty: EMPTY_FILTERS,
			fromUrl: (s) => ({ search: s.search ?? "", category: s.category ?? "", organizationTypeId: s.organization_type_id ?? "" }),
			toUrl: (f) => ({
				search: f.search.trim() || undefined,
				category: f.category.trim() || undefined,
				organization_type_id: f.organizationTypeId || undefined,
			}),
		},
		summary: { range: (v) => t("pagination.mainSummary", v), empty: t("pagination.mainEmpty") },
	});
	const types = useQuery(organizationTypeOptionsOptions());
	// Unfiltered counts — per organisation type (no-type bucket = "every type"), sub-functions — in one aggregate statement.
	const stats = useQuery(mainFunctionStatsOptions());
	const buckets = stats.data?.by_organization_type;
	const del = useConfirmDelete<MainFunctionListItem>({
		remove: (r) => deleteMainFunction(r.main_function_id),
		invalidate: invalidateFunctions,
		deleted: (r) => t("mainDelete.deleted", { name: r.main_function_name }),
	});

	const total = page.list.data?.total;
	// The list filter takes a type id only — no "none" — so the no-type bucket is a stat, not a chip.
	const typeOptions = organizationTypeChipOptions(
		types.data,
		buckets?.filter((b) => b.organization_type_id !== null),
		"",
		search.organization_type_id,
	);
	const sort = sortSelect<MainFunctionSortKey>(
		search,
		"sort_order:asc",
		[
			{ value: "sort_order:asc", label: t("mainList.sortCardOrder") },
			{ value: "sort_order:desc", label: t("mainList.sortCardOrderDesc") },
			{ value: "code:asc", label: tc("sort.codeAsc") },
			{ value: "code:desc", label: tc("sort.codeDesc") },
			{ value: "name:asc", label: tc("sort.nameAsc") },
			{ value: "name:desc", label: tc("sort.nameDesc") },
			{ value: "category:asc", label: t("mainList.sortCategoryAsc") },
			{ value: "category:desc", label: t("mainList.sortCategoryDesc") },
			{ value: "created_at:desc", label: tc("sort.newest") },
			{ value: "created_at:asc", label: tc("sort.oldest") },
		],
		page.patch,
	);

	const columns: ColumnDef<MainFunctionListItem, MainFunctionSortKey>[] = [
		{
			key: "name",
			header: t("mainTable.name"),
			sortable: true,
			lockVisible: true,
			minWidth: "16rem",
			cell: (r) => (
				<span className="flex min-w-0 items-center gap-[0.75rem]">
					<BannerSwatch color={r.card_color} ink={r.card_ink_color} label={r.card_color === null ? t("mainList.noBanner") : t("mainList.bannerTitle", { color: r.card_color, ink: r.card_ink_color ?? WHITE })} />
					<PrimaryCell sub={r.main_function_code}>{r.main_function_name}</PrimaryCell>
				</span>
			),
		},
		{
			key: "organization_type" as MainFunctionSortKey,
			header: t("mainList.type"),
			minWidth: "12rem",
			cell: (r) =>
				r.organization_type ? (
					<ListTag>{r.organization_type.name}</ListTag>
				) : (
					<span className="text-[0.78125rem] text-gms-ink-3" title={t("mainList.everyTypeTitle")}>
						{t("mainList.everyType")}
					</span>
				),
		},
		{
			key: "category",
			header: t("mainTable.category"),
			sortable: true,
			minWidth: "9rem",
			// A category is free text, so it filters from the row: click one to see its siblings.
			cell: (r) =>
				r.main_function_category !== null ? (
					<button
						type="button"
						className="cursor-pointer text-left hover:text-gms-navy hover:underline"
						title={t("mainList.categoryFilterTitle", { category: r.main_function_category })}
						onClick={() => page.patch({ category: r.main_function_category ?? undefined })}
					>
						{r.main_function_category}
					</button>
				) : (
					<span className="text-base-content/30">—</span>
				),
		},
		{ key: "sort_order", header: t("mainTable.sortOrder"), sortable: true, width: "5.5rem", numeric: true, cell: (r) => <span className="tabular-nums">{r.sort_order}</span> },
		{
			key: "sub_functions_count" as MainFunctionSortKey,
			header: t("mainTable.subFunctions"),
			width: "8rem",
			numeric: true,
			cell: (r) =>
				r.sub_functions_count > 0 ? (
					<Link
						to="/settings/functions/$mainFunctionId"
						params={{ mainFunctionId: r.main_function_id }}
						search={{}}
						className="font-semibold tabular-nums text-gms-navy hover:underline"
						title={t("mainTable.subFunctionsTitle")}
					>
						{formatNumber(r.sub_functions_count)}
					</Link>
				) : (
					<span className="text-base-content/30">{formatNumber(0)}</span>
				),
		},
		usageColumn("cards_count", t("mainTable.cards"), t("mainTable.cardsTitle")),
		timestampColumn("created_at", t("mainTable.created"), true),
	];

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<ListHeader
				title={t("page.title")}
				count={total === undefined ? undefined : t("mainList.count", { count: total, n: formatNumber(total) })}
				description={<Trans t={t} i18nKey="page.intro" components={{ strong: <strong className="font-semibold text-gms-ink-2" /> }} />}
				actionLabel={t("page.addMainFunction")}
				onAction={isSuper ? () => setForm({ mode: "create" }) : undefined}
			/>

			<StatsStrip
				label={tc("list.summary")}
				cells={[
					{ key: "total", label: t("mainList.statsTotal"), value: total ?? "—", note: page.hasFilters ? t("mainList.statsMatching") : undefined },
					...topOrganizationTypeCells(buckets),
					...(stats.data
						? [
								{ key: "every-type", label: t("mainList.everyType"), value: stats.data.by_organization_type.find((b) => b.organization_type_id === null)?.count ?? 0 },
								{ key: "sub-functions", label: t("mainList.statsSubFunctions"), value: stats.data.sub_functions_total },
							]
						: []),
				]}
			/>

			<ListToolbar
				search={search.search ?? ""}
				onSearch={(v) => page.patch({ search: v || undefined })}
				searchPlaceholder={t("mainList.searchPlaceholder")}
				sort={sort}
				chips={[
					{
						kind: "options",
						key: "type",
						label: t("mainList.type"),
						options: typeOptions,
						value: search.organization_type_id ? [search.organization_type_id] : [],
						onChange: (v) => page.patch({ organization_type_id: v[0] ?? undefined }),
					},
					// Shown only while a category (picked from a row) narrows the list; click to drop it.
					...(search.category
						? [
								{
									kind: "toggle" as const,
									key: "category",
									label: t("mainList.categoryChip", { category: search.category }),
									pressed: true,
									onChange: () => page.patch({ category: undefined }),
								},
							]
						: []),
				]}
				onClearAll={page.clearFilters}
			/>

			<DataTable<MainFunctionListItem, MainFunctionSortKey>
				{...page.table}
				variant="list"
				columns={columns}
				rowKey={(r) => r.main_function_id}
				empty={page.emptyState({
					filtered: { title: t("empty.mainFilteredTitle"), description: t("empty.mainFilteredDescription") },
					none: { title: t("empty.mainNoneTitle"), description: t("empty.mainNoneDescription") },
					clearLabel: t("empty.clearFilters"),
				})}
				rowActions={(r) => (
					<EditDeleteActions
						editLabel={t("mainTable.editLabel", { name: r.main_function_name })}
						onEdit={isSuper ? () => setForm({ mode: "edit", mainFunction: r }) : undefined}
						deleteLabel={t("mainTable.deleteLabel", { name: r.main_function_name })}
						onDelete={isSuper ? () => del.ask(r) : undefined}
						canDelete={r.sub_functions_count === 0 && r.cards_count === 0 && r.scopes_count === 0}
						variant="list"
					>
						{/* The drill-down is read-only for non-admins, so everyone gets the eye. */}
						<IconButton
							label={t("mainTable.viewLabel", { name: r.main_function_name })}
							icon={<Icons.Eye />}
							size="xs"
							variant="row"
							onClick={() => void navigate({ to: "/settings/functions/$mainFunctionId", params: { mainFunctionId: r.main_function_id }, search: {} })}
						/>
					</EditDeleteActions>
				)}
			/>

			{isSuper && <MainFunctionFormModal target={form} onClose={() => setForm(null)} />}
			{isSuper && (
				<ConfirmDeleteModal
					{...del.modal}
					title={t("mainDelete.title")}
					description={<Trans t={t} i18nKey="mainDelete.description" values={{ name: del.row?.main_function_name ?? "" }} components={{ strong: <strong className="font-semibold text-base-content" /> }} />}
					confirmLabel={t("mainDelete.confirm")}
					errorPrefix={t("mainDelete.failed")}
				/>
			)}
		</div>
	);
}

/** A banner with no ink colour set prints white text. */
const WHITE = "#ffffff";

/**
 * The card's banner in miniature: its colour, with a bar of its ink colour
 * where the category name prints (white when none is set). A dashed empty
 * box when the category has no colour yet.
 */
function BannerSwatch({ color, ink, label }: { color: string | null; ink: string | null; label: string }) {
	if (color === null) {
		return <span role="img" aria-label={label} title={label} className="h-[1.5rem] w-[2.25rem] shrink-0 rounded-[0.25rem] border border-dashed border-gms-line-2 bg-gms-soft" />;
	}
	return (
		<span
			role="img"
			aria-label={label}
			title={label}
			className="flex h-[1.5rem] w-[2.25rem] shrink-0 items-center justify-center rounded-[0.25rem] shadow-[inset_0_0_0_1px_rgba(18,20,63,0.12)]"
			style={{ backgroundColor: color }}
		>
			<span className="h-[0.25rem] w-[1.25rem] rounded-full" style={{ backgroundColor: ink ?? WHITE }} />
		</span>
	);
}

/** The colour bar printed on cards of this function; a muted empty square when none is set. */
export function CardColorSwatch({ color, className = "size-[1.125rem]" }: { color: string | null; className?: string }) {
	return color !== null ? (
		<span aria-hidden className={`${className} shrink-0 rounded-[0.25rem] border border-base-300`} style={{ backgroundColor: color }} />
	) : (
		<span aria-hidden className={`${className} shrink-0 rounded-[0.25rem] border border-base-300 bg-base-200/50`} />
	);
}
